/**
 * エディター内のエラー箇所を視覚的にハイライト
 */
export class ErrorHighlighter {
  private decorations: string[] = []
  private gutterDecorations: string[] = []
  
  /**
   * エラーをハイライト
   */
  highlightErrors(
    editor: any, // monaco.editor.IStandaloneCodeEditor
    errors: Array<{ line: number; column?: number; message: string }>
  ): void {
    // 既存のデコレーションをクリア
    this.clearHighlights(editor)
    
    if (errors.length === 0) return
    
    const monaco = (window as any).monaco
    
    // Monacoが初期化されていない場合は何もしない
    if (!monaco || !monaco.Range) {
      console.warn('Monaco is not initialized yet')
      return
    }
    
    // エラー行のデコレーション
    const errorDecorations = errors.map(error => ({
      range: new monaco.Range(error.line, 1, error.line, 1000),
      options: {
        isWholeLine: true,
        className: 'error-line-highlight',
        glyphMarginClassName: 'error-glyph',
        hoverMessage: {
          value: `**エラー:** ${error.message}`
        },
        minimap: {
          color: '#ef4444',
          position: monaco.editor.MinimapPosition.Inline
        }
      }
    }))
    
    // 波線のデコレーション
    const squiggleDecorations = errors.map(error => {
      const model = editor.getModel()
      const line = model.getLineContent(error.line)
      const startColumn = error.column || 1
      const endColumn = error.column ? error.column + 10 : line.length + 1
      
      return {
        range: new monaco.Range(error.line, startColumn, error.line, endColumn),
        options: {
          className: 'error-squiggly',
          hoverMessage: {
            value: error.message
          }
        }
      }
    })
    
    // デコレーションを適用
    this.decorations = editor.deltaDecorations(
      this.decorations,
      [...errorDecorations, ...squiggleDecorations]
    )
    
    // 最初のエラー行にジャンプ
    if (errors.length > 0) {
      this.jumpToError(editor, errors[0].line)
    }
  }
  
  /**
   * ハイライトをクリア
   */
  clearHighlights(editor: any): void {
    this.decorations = editor.deltaDecorations(this.decorations, [])
  }
  
  /**
   * エラー行にジャンプ
   */
  private jumpToError(editor: any, line: number): void {
    const monaco = (window as any).monaco
    
    // Monacoが初期化されていない場合は何もしない
    if (!monaco || !monaco.Range) {
      console.warn('Monaco is not initialized yet')
      return
    }
    
    // スムーズにスクロール
    editor.revealLineInCenterIfOutsideViewport(line, monaco.editor.ScrollType.Smooth)
    
    // 一時的にハイライト効果を追加
    const tempDecoration = editor.deltaDecorations([], [{
      range: new monaco.Range(line, 1, line, 1),
      options: {
        isWholeLine: true,
        className: 'error-line-pulse'
      }
    }])
    
    // 2秒後にハイライトを削除
    setTimeout(() => {
      editor.deltaDecorations(tempDecoration, [])
    }, 2000)
  }
  
  /**
   * スタイルを設定
   */
  static setupStyles(): void {
    const style = document.createElement('style')
    style.textContent = `
      /* エラー行のハイライト */
      .error-line-highlight {
        background-color: rgba(239, 68, 68, 0.1) !important;
        border-left: 3px solid var(--error-color) !important;
      }
      
      /* エラーグリフ */
      .error-glyph {
        background-color: var(--error-color);
        border-radius: 50%;
        width: 12px !important;
        height: 12px !important;
        margin-left: 2px !important;
        margin-top: 3px !important;
        position: relative;
      }
      
      .error-glyph::after {
        content: '!';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: white;
        font-size: 8px;
        font-weight: bold;
      }
      
      /* エラー波線 */
      .error-squiggly {
        text-decoration: underline wavy var(--error-color) !important;
        text-underline-offset: 3px !important;
      }
      
      /* パルスアニメーション */
      .error-line-pulse {
        animation: errorLinePulse 2s ease-out;
      }
      
      @keyframes errorLinePulse {
        0% {
          background-color: rgba(239, 68, 68, 0.3);
          box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
        }
        50% {
          background-color: rgba(239, 68, 68, 0.2);
          box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
        }
        100% {
          background-color: rgba(239, 68, 68, 0.1);
          box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
        }
      }
      
      /* エラーツールチップ */
      .monaco-hover-content {
        max-width: 400px !important;
      }
      
      .monaco-hover-content strong {
        color: var(--error-color);
      }
    `
    document.head.appendChild(style)
  }
}